import { useState } from 'react';
import toast from 'react-hot-toast';
import { cn } from '@/lib/cn';
import { Dialog } from '@/components/ui/Dialog';
import { ApiError, saveFailureMessage } from '@/lib/apiErrors';
import { todayInPH, displayPhDate, displayPhTime } from '@/lib/phDate';
import { parseTypedAmount } from '@/lib/number';
import { bigAmountLimit, fitsPrecision, PRECISION_HINT } from '@/lib/applicationAmount';
import type { ApplicationResponse, EditHistoryResponse } from '@/types/generated/openapi';
import { amountText, categoryLabel } from './format';
import { useApplicationHistory, useFarmProducts, useReadingReasons } from './data';
import { LOCK_MESSAGE, useApplicationActions, type ApplicationChanges } from './useApplicationActions';

const inputCls =
  'min-h-[44px] w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20';
const ROUTINE = 'Routine';
const OTHER = 'other';

interface Props {
  farmId: string;
  cropId: string;
  row: ApplicationResponse;
  canDelete: boolean;
  /** Supervisor+: shows who changed what. */
  canSeeHistory: boolean;
  /** Open straight on the delete confirmation (the row's Delete button). */
  startWithDelete?: boolean;
  onClose: () => void;
}

export function ApplicationEditDialog(props: Props) {
  return (
    <Dialog open onClose={props.onClose} title="Edit application" size="md">
      <EditForm {...props} />
    </Dialog>
  );
}

const FIELD_LABEL: Record<string, string> = {
  amount: 'Amount',
  application_date: 'Date',
  product_name: 'Product',
  unit: 'Unit',
  reason: 'Reason',
  notes: 'Notes',
};
// product_id and product_category change together with product_name; showing them is noise.
const HIDDEN_FIELDS = new Set(['product_id', 'product_category', 'inventory_item_id']);

function shown(v: unknown): string {
  return v === null || v === undefined || v === '' ? 'blank' : String(v);
}

function historyLine(h: EditHistoryResponse): string {
  if (h.action === 'delete') return 'Deleted';
  return Object.keys(h.new_data)
    .filter((k) => !HIDDEN_FIELDS.has(k))
    .map((k) => `${FIELD_LABEL[k] ?? k}: ${shown(h.previous_data[k])} → ${shown(h.new_data[k])}`)
    .join('; ');
}

function History({ farmId, id }: { farmId: string; id: string }) {
  const { data, isLoading, isError } = useApplicationHistory(farmId, id, true);
  return (
    <div className="mt-4 border-t border-gray-100 pt-3">
      <h3 className="text-[13px] font-semibold text-gray-700">History</h3>
      {isLoading ? (
        <p className="mt-1 text-sm text-gray-500">Loading…</p>
      ) : isError ? (
        <p className="mt-1 text-sm text-gray-500">Couldn't load the history.</p>
      ) : !data || data.length === 0 ? (
        <p className="mt-1 text-sm text-gray-500">No changes recorded.</p>
      ) : (
        <ul className="mt-1 space-y-1.5 text-sm">
          {data.map((h) => {
            const at = new Date(h.changed_at);
            return (
              <li key={h.id} className="text-gray-700" data-history-entry>
                <span className="text-xs text-gray-500">
                  {h.changed_by_name ?? 'Someone'} · {displayPhDate(at, { month: 'short', day: 'numeric' })}, {displayPhTime(at)}
                </span>
                <br />
                {historyLine(h)}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function EditForm({ farmId, cropId, row, canDelete, canSeeHistory, startWithDelete, onClose }: Props) {
  const { update, remove } = useApplicationActions(farmId);
  const { data: products = [] } = useFarmProducts(farmId);
  const readingReasons = useReadingReasons(farmId, cropId, row.application_date);

  const [amount, setAmount] = useState(String(Number(row.amount ?? 0)));
  const [date, setDate] = useState(row.application_date);
  const [productId, setProductId] = useState(row.product_id ?? '');
  const [reasonPick, setReasonPick] = useState(!row.reason ? '' : row.reason === ROUTINE ? ROUTINE : OTHER);
  const [reasonText, setReasonText] = useState(row.reason && row.reason !== ROUTINE ? row.reason : '');
  const [notes, setNotes] = useState(row.notes ?? '');
  const [confirmDelete, setConfirmDelete] = useState(!!startWithDelete && canDelete);
  const [bigOk, setBigOk] = useState<number | null>(null);

  // A different product may only be a same-unit one: the amount keeps its meaning.
  const sameUnit = products.filter(
    (p) => p.is_active && (p.id === row.product_id || p.unit.toLowerCase() === (row.unit ?? '').toLowerCase())
  );
  const parsed = parseTypedAmount(amount);
  const amountError =
    'error' in parsed
      ? parsed.error
      : parsed.value === null
        ? 'Enter an amount'
        : !(parsed.value > 0)
          ? 'Amount must be more than 0'
          : !fitsPrecision(String(parsed.value))
            ? PRECISION_HINT
            : null;
  const amountNum = 'value' in parsed && parsed.value !== null ? parsed.value : 0;
  const reason = reasonPick === OTHER ? reasonText.trim() : reasonPick;
  const tooBig = !amountError && amountNum > bigAmountLimit(Number(row.amount) || 0, row.unit ?? '');

  const changes: ApplicationChanges = {};
  if (!amountError && amountNum !== Number(row.amount)) changes.amount = amountNum;
  if (date !== row.application_date) changes.application_date = date;
  if (productId && productId !== (row.product_id ?? '')) changes.product_id = productId;
  if (reason !== (row.reason ?? '')) changes.reason = reason || null;
  if (notes.trim() !== (row.notes ?? '')) changes.notes = notes.trim() || null;
  const dirty = Object.keys(changes).length > 0;

  const busy = update.isPending || remove.isPending;
  const error = update.error ?? remove.error;
  const conflict = error instanceof ApiError && error.status === 409 && LOCK_MESSAGE.test(error.message);

  const save = () => {
    if (!dirty || amountError) return;
    if (changes.amount !== undefined && tooBig && bigOk !== amountNum) {
      setBigOk(amountNum);
      return;
    }
    update.mutate(
      { row, changes },
      {
        onSuccess: () => {
          toast.success('Saved');
          onClose();
        },
      }
    );
  };

  const doDelete = () =>
    remove.mutate(row, {
      onSuccess: () => {
        toast.success('Deleted');
        onClose();
      },
    });

  if (confirmDelete) {
    return (
      <div data-delete-confirm>
        <p className="text-sm text-gray-800">
          Delete <b>{row.product_name || 'this entry'}</b> {amountText(row.amount, row.unit)} on{' '}
          {displayPhDate(new Date(`${row.application_date}T12:00:00+08:00`), { month: 'short', day: 'numeric' })}? It leaves
          the journal and the crop totals. The change is recorded.
        </p>
        {error && (
          <p role="alert" className="mt-3 text-sm text-red-700">
            Not deleted: {saveFailureMessage(error, 'try again')}
          </p>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => (startWithDelete ? onClose() : setConfirmDelete(false))}
            disabled={busy}
            className="min-h-[44px] rounded-lg border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-700"
          >
            Keep it
          </button>
          <button
            type="button"
            onClick={doDelete}
            disabled={busy}
            className="min-h-[44px] rounded-lg bg-red-600 px-4 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
          >
            {remove.isPending ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      className="flex flex-col gap-3.5"
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
    >
      <p className="text-sm text-gray-600">
        {row.product_name || 'Unnamed'} · {categoryLabel(row.product_category)}
      </p>

      {sameUnit.length > 1 && (
        <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-gray-700">
          Product
          <select className={inputCls} value={productId} onChange={(e) => setProductId(e.target.value)}>
            {!row.product_id && <option value="">{row.product_name || 'Not on the farm list'}</option>}
            {sameUnit.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.unit})
              </option>
            ))}
          </select>
        </label>
      )}

      <div className="grid grid-cols-2 gap-2.5">
        <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-gray-700">
          Amount{row.unit ? ` (${row.unit})` : ''}
          <input
            className={cn(inputCls, 'tabular-nums')}
            inputMode="decimal"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setBigOk(null);
            }}
            aria-invalid={!!amountError}
            aria-describedby={amountError ? 'ap-edit-amount-error' : undefined}
          />
          {amountError && (
            <span id="ap-edit-amount-error" role="alert" className="text-xs font-normal text-red-600">
              {amountError}
            </span>
          )}
        </label>
        <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-gray-700">
          When
          <input type="date" className={inputCls} value={date} max={todayInPH()} onChange={(e) => e.target.value && setDate(e.target.value)} />
        </label>
      </div>

      <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-gray-700">
        Reason
        <select className={inputCls} value={reasonPick} onChange={(e) => setReasonPick(e.target.value)}>
          <option value="">No reason given</option>
          {readingReasons.map((r) => (
            <option key={r} value={r}>
              Linked to reading: {r}
            </option>
          ))}
          <option value={ROUTINE}>Routine</option>
          <option value={OTHER}>Other…</option>
        </select>
      </label>
      {reasonPick === OTHER && (
        <input className={inputCls} value={reasonText} onChange={(e) => setReasonText(e.target.value)} placeholder="What for?" aria-label="Other reason" />
      )}

      <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-gray-700">
        Notes
        <input className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
      </label>

      {bigOk !== null && bigOk === amountNum && tooBig && !update.isPending && (
        <p role="alert" className="text-sm text-amber-800">
          {amountText(amountNum, row.unit)} is a lot for this product. Press Save again if it is right.
        </p>
      )}
      {error && (
        <p role="alert" className="text-sm text-red-700">
          {conflict ? 'Changed by someone else just now. Close this and open the entry again.' : `Not saved: ${saveFailureMessage(error, 'try again')}`}
        </p>
      )}

      <div className="flex items-center justify-between gap-2">
        {canDelete ? (
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            disabled={busy}
            className="min-h-[44px] rounded-lg px-3 text-sm font-semibold text-red-700 hover:bg-red-50"
          >
            Delete…
          </button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="min-h-[44px] rounded-lg border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!dirty || !!amountError || busy}
            className="min-h-[44px] rounded-lg bg-primary-600 px-4 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {update.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {canSeeHistory && <History farmId={farmId} id={row.id} />}
    </form>
  );
}
